import i18next from 'i18next'
import React from 'react'
import {
  AsyncState,
  Fun,
  IOWidget,
  Updater,
  any,
  async,
  fromJSX,
  loadedAsyncState,
  onlyIf,
} from 'widgets-for-react'
import { UserSubscriptions } from '../../../subscription/subscriptionState'
import { blogTeasersWidget } from '../blogs/blogTeasers/blogTeasersWidget'
import { CollectionTeasersLayout } from '../collections/collectionTeasers/collectionTeasersLayout'
import { CollectionsLoaderState } from '../collections/collectionsLoader/collectionsLoaderState'
import { componentSaverWidget } from '../components/componentSaver/componentSaverWidget'
import { componentTeasersWidget } from '../components/componentTeasers/componentTeasersWidget'
import { dishTeasersWidget } from '../dishes/dishTeasers/dishTeasersWidget'
import { FiltersLoaderState } from '../filters/filtersLoader/filtersLoaderState'
import { TopFilters, filteredUrl } from '../filters/filtersUtils'
import { topFiltersWidget } from '../filters/topFilters/topFiltersWidget'
import { knowledgeTeasersWidget } from '../knowledge/knowledgeTeasers/knowledgeTeasersWidget'
import { paginationWidget } from '../pagination/paginationWidget'
import { paywallWidget } from '../paywall/paywallWidget'
import { RootLayout } from '../rootLayout'
import { ViewListenerState } from '../viewListener/viewListenerState'
import {
  SearchHeader,
  SearchResultsAndPaginationWrapper,
  SearchResultsWrapper,
  SearchTabs,
} from './searchLayout'
import {
  SearchState,
  SearchTab,
  searchStateUpdaters,
  searchTabs,
} from './searchState'

export const searchResultsPerPage = (searchTab: SearchTab): number => {
  switch (searchTab) {
    case 'components':
      return 24
    case 'blogs':
      return 10
    case 'knowledge':
      return 16
    case 'collections':
    case 'dishes':
      return 12
  }
}

export const searchWidget =
  (
    searchTab: SearchTab,
    term: string,
    topFilters: TopFilters,
    page: number,
    isLoggedIn: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    collectionsState: CollectionsLoaderState,
    categoriesState: FiltersLoaderState,
    viewListenerState: ViewListenerState
  ): IOWidget<
    SearchState,
    Fun<SearchState, SearchState & { reloadCollections?: boolean }>
  > =>
  (searchState) =>
    paywallWidget(isLoggedIn, userSubscriptionsState)(
      searchBaseWidget(
        searchTab,
        term,
        topFilters,
        page,
        collectionsState,
        true,
        isLoggedIn,
        categoriesState,
        viewListenerState
      )(searchState),
      searchBaseWidget(
        searchTab,
        term,
        topFilters,
        page,
        collectionsState,
        false,
        isLoggedIn,
        categoriesState,
        viewListenerState
      )(searchState)
    )

const searchBaseWidget =
  (
    searchTab: SearchTab,
    term: string,
    topFilters: TopFilters,
    page: number,
    collectionsState: CollectionsLoaderState,
    paywalled: boolean,
    isLoggedIn: boolean,
    filtersLoaderState: FiltersLoaderState,
    viewListenerState: ViewListenerState
  ): IOWidget<
    SearchState,
    Fun<SearchState, SearchState & { reloadCollections?: boolean }>
  > =>
  (state) => {
    const {
      componentsCountState,
      knowledgeCountState,
      blogsCountState,
      collectionsCountState,
      dishesCountState,
    } = {
      componentsCountState: state.searchTabs.components.totalCount,
      knowledgeCountState: state.searchTabs.knowledge.totalCount,
      blogsCountState: state.searchTabs.blogs.totalCount,
      collectionsCountState: state.searchTabs.collections.totalCount,
      dishesCountState: state.searchTabs.dishes.totalCount,
    }
    const searchResultCounts = {
      components:
        componentsCountState.kind === 'loaded' ? componentsCountState.value : 0,
      knowledge:
        knowledgeCountState.kind === 'loaded' ? knowledgeCountState.value : 0,
      blogs: blogsCountState.kind === 'loaded' ? blogsCountState.value : 0,
      collections:
        collectionsCountState.kind === 'loaded'
          ? collectionsCountState.value
          : 0,
      dishes: dishesCountState.kind === 'loaded' ? dishesCountState.value : 0,
    }
    return any<Fun<SearchState, SearchState & { reloadCollections?: boolean }>>(
      [
        any(
          searchTabs.map((tab) =>
            async(state.searchTabs[tab].totalCount).map(
              searchStateUpdaters.totalCountUpdater(tab)
            )
          )
        ),
        any([
          fromJSX(() => (
            <SearchTabs
              activeTab={searchTab}
              term={term}
              topFilters={topFilters}
              searchResultsCounts={searchResultCounts}
            />
          )),
          searchResultsWidget(
            searchTab,
            term,
            topFilters,
            collectionsState,
            paywalled,
            isLoggedIn,
            searchResultCounts,
            filtersLoaderState,
            viewListenerState
          )(state).wrapHTML((element) => (
            <SearchResultsWrapper searchTab={searchTab} child={element} />
          )),
          onlyIf(
            !paywalled,
            paginationWidget(
              page - 1,
              searchResultsPerPage(searchTab),
              (newPage: number) =>
                filteredUrl(`/${i18next.language}/search`)({
                  term: term,
                  topFilters: topFilters,
                  searchTab: searchTab,
                  page: newPage + 1,
                })
            )(state.paginationState).map(
              searchStateUpdaters.paginationStateUpdater
            )
          ),
        ]),
      ]
    ).wrapHTML((element) => (
      <SearchResultsAndPaginationWrapper child={element} />
    ))
  }

const searchResultsWidget =
  (
    searchTab: SearchTab,
    term: string,
    topFilters: TopFilters,
    collectionsState: CollectionsLoaderState,
    paywalled: boolean,
    isLoggedIn: boolean,
    searchResultsCounts: { [searchTab in SearchTab]?: number },
    filtersLoaderState: FiltersLoaderState,
    viewListenerState: ViewListenerState
  ): IOWidget<
    SearchState,
    Fun<SearchState, SearchState & { reloadCollections?: boolean }>
  > =>
  (state) => {
    const resultCount = searchResultsCounts[searchTab] || 0;
    const title = i18next.t(
          resultCount === 1 ? 'search:searchResultsFor' : 'search:searchResultsFor_plural', {
              count: resultCount,
              term: `'${term}'`,
        });
    switch (searchTab) {
      case 'components':
        if (filtersLoaderState.kind === 'loading')
          return fromJSX(() => (
            <RootLayout.Loader hasWrapper={true} showText={true} />
          ))

        if (filtersLoaderState.kind === 'loaded') {
          const filters = filtersLoaderState.value

          return any([
            fromJSX(() => <SearchHeader title={title} />),
            topFiltersWidget(
              filters.Techniques,
              filters.Appearances,
              filters.Textures,
              filters.Allergens,
              [
                {
                  Translation: i18next.t(
                    'componentFilter:basicComponents'
                  ),
                  Title: 'basic',
                },
                {
                  Translation: i18next.t(
                    'componentFilter:ownComponents'
                  ),
                  Title: 'own',
                },
              ],
              {},
              topFilters,
              viewListenerState,
              state.paginationState,
              filteredUrl(
                `/${i18next.language === 'nl' ? 'nl/search' : 'en/search'}`
              ),
              term,
              searchTab
            )(state.topFiltersState).map(
              searchStateUpdaters.topFiltersStateUpdater
            ),
            componentTeasersWidget(
              collectionsState,
              'search-results',
              isLoggedIn ? 'link-save' : 'link-only'
            )(
              paywalled && state.searchTabs.components.results.kind == 'loaded'
                ? loadedAsyncState(
                    state.searchTabs.components.results.value.slice(0, 12)
                  )
                : state.searchTabs.components.results
            ).map(searchStateUpdaters.componentTeasersStateUpdater),
            componentSaverWidget(collectionsState)(
              state.componentSaverState
            ).map(searchStateUpdaters.componentSaverStateUpdater),
          ])
        }
      case 'dishes':
        return dishTeasersWidget(
          'search-results',
          title
        )(
          paywalled && state.searchTabs.dishes.results.kind === 'loaded'
            ? loadedAsyncState(
                state.searchTabs.dishes.results.value.slice(0, 1)
              )
            : state.searchTabs.dishes.results
        ).map(searchStateUpdaters.dishesTeasersStateUpdater)
      case 'knowledge':
        return knowledgeTeasersWidget(
          'search-results',
          title
        )(state.searchTabs.knowledge.results).map(
          searchStateUpdaters.knowledgeTeasersStateUpdater
        )
      case 'blogs':
        return blogTeasersWidget(
          'search-results',
          title
        )(
          paywalled && state.searchTabs.blogs.results.kind === 'loaded'
            ? loadedAsyncState(state.searchTabs.blogs.results.value.slice(0, 1))
            : state.searchTabs.blogs.results
        ).map(searchStateUpdaters.blogTeasersStateUpdater)
      case 'collections':
        const collectionResults = state.searchTabs.collections.results
        return any<Updater<CollectionsLoaderState>>([
          async(state.searchTabs.collections.results),
          fromJSX(() => (
            <CollectionTeasersLayout
              kind="search-results"
              title={title}
              collections={
                collectionResults.kind == 'loaded'
                  ? paywalled
                    ? collectionResults.value.slice(0, 4)
                    : collectionResults.value
                  : collectionResults.kind == 'loading'
                  ? 'loading'
                  : 'error'
              }
            />
          )),
        ]).map(searchStateUpdaters.collectionTeasersStateUpdater)
    }
  }
